import { Button, Spin } from 'antd';
import React from 'react';
import Icon from '../../../components/icon/Icon';
import OfflineStatusControl from '../../../components/OfflineStatusControl';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { CountdownAnchor, readCountdown, startCountdown } from '../../../helpers/countdownClock';
import { getEventCloudLabel, refreshMatchGoals } from '../../../helpers/EventHelper';
import { payloadFromCommand } from '../../../helpers/eventMutations';
import { eventSubmissionQueue } from '../../../helpers/eventSubmissionQueue';
import { applyRemoteDelete, MatchEventsHub } from '../../../helpers/matchEventsHub';
import {
    buildExclusionCommand,
    buildShotCommand,
    buildTimeoutCommand,
    MobileEventTiming,
} from '../../../helpers/mobileEventBuilders';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import { newClientEventKey } from '../../../helpers/offlineEventStore';
import {
    adjustMatchGoalsForShot,
    applyOutboxRowsToState,
    bumpMatchForPendingGoals,
    getClientEventKey,
    isPendingEvent,
    markPending,
    mergeServerWithLocalPending,
    optimisticEventFromCommand,
    removePendingByKey,
    replacePendingWithServer,
    upsertServerEvent,
} from '../../../helpers/pendingEventHelpers';
import {
    CreateEventCommand,
    EventPeriodEnum,
    EventsClient,
    EventTypeEnum,
    EventVm,
    ExclusionTypeEnum,
    MatchDetailVm,
    MatchesClient,
    MatchPlayerVm,
    ShotTypeEnum,
} from '../../../utils/api';
import { OvertimeUiMode } from '../../match-report/match-clock/periodLabels';
import MobileShell from '../MobileShell';
import ActionButtons from './components/ActionButtons';
import ClockEditor from './components/ClockEditor';
import EventEntryPanel, { FoulModifier, GoalType } from './components/EventEntryPanel';
import PossessionToggle, { Possession } from './components/PossessionToggle';
import ScoreHeader from './components/ScoreHeader';
import ShotClockControls from './components/ShotClockControls';
import { Props } from './index';

const INITIAL_TIME = 8 * 60;
const SHOT_MAX = 28;
const SHOT_PARTIAL = 18;
/** ~10Hz so mobile tenths update while running. */
const CLOCK_TICK_MS = 100;

type PendingFlow = 'goal' | 'foul' | null;
type ClockEditing = 'main' | 'shot' | null;
type EventMarker = 'goal' | 'foul' | 'timeout' | 'miss';

/** Shot clock can never exceed the remaining game-clock time. */
const clampShot = (target: number, mainRemaining: number): number =>
    Math.max(0, Math.min(target, mainRemaining));

interface State {
    match?: MatchDetailVm;
    events: EventVm[];
    loading: boolean;
    currentPeriod: EventPeriodEnum;
    overtimeMode: OvertimeUiMode;
    timeRemaining: number;
    shotClock: number;
    clockRunning: boolean;
    /** Starts unset; operator selects after the swim-off. */
    possession: Possession;
    /** Open event sub-panel (cap pick). */
    pendingFlow: PendingFlow;
    /** Team whose caps the open panel shows (scorer for goal, defender for foul). */
    flowSide: 'home' | 'away' | null;
    goalType: GoalType;
    foulModifier: FoulModifier;
    /** Game-clock value captured when the action was tapped. */
    eventTimeRemaining: number;
    clockEditing: ClockEditing;
}

class MobileMatchReport extends React.PureComponent<Props, State> {
    private timer: NodeJS.Timeout | null = null;

    private isMounted_ = false;

    // Realtime sync connection to /hubs/match (events only).
    private eventsHub = new MatchEventsHub();

    private unsubOutbox: (() => void) | null = null;

    // Lazily-created Web Audio context for the clock buzzer. Unlocked on the
    // start tap so mobile autoplay policies don't block it later.
    private audioCtx: AudioContext | null = null;

    /** Wall-clock anchors so stop/start keeps sub-second remainder. */
    private mainAnchor: CountdownAnchor | null = null;

    private shotAnchor: CountdownAnchor | null = null;

    public constructor(props: Props) {
        super(props);
        this.state = {
            events: [],
            loading: true,
            currentPeriod: EventPeriodEnum.First_Period,
            overtimeMode: 'play',
            timeRemaining: INITIAL_TIME,
            shotClock: SHOT_MAX,
            clockRunning: false,
            possession: null,
            pendingFlow: null,
            flowSide: null,
            goalType: 'regular',
            foulModifier: null,
            eventTimeRemaining: INITIAL_TIME,
            clockEditing: null,
        };
    }

    public componentDidMount = (): void => {
        this.isMounted_ = true;
        this.restoreClock();
        this.getMatch();
        this.connectEventsHub();
        eventSubmissionQueue.start();
        this.unsubOutbox = eventSubmissionQueue.subscribe((e) => {
            if (!this.isMounted_) return;
            if (e.type === 'wentOnline' || e.type === 'syncRequested') {
                // Outbox already drained (or Arena still unreachable). Refetch once.
                void this.getMatch();
                return;
            }
            const matchId = this.state.match?.id;
            if (!matchId) return;
            if (e.type === 'published' && e.matchId === matchId) {
                if (e.op === 'delete') {
                    this.setState((prev) => {
                        const deleted = prev.events.find(
                            (ev) =>
                                ev.id === e.result.id || getClientEventKey(ev) === e.clientEventKey
                        );
                        const events = applyRemoteDelete(
                            removePendingByKey(prev.events, e.clientEventKey),
                            e.result
                        );
                        let nextMatch = prev.match;
                        if (
                            deleted?.typeId === EventTypeEnum.Shot &&
                            deleted.shot?.isGoal &&
                            prev.match
                        ) {
                            nextMatch = adjustMatchGoalsForShot(
                                prev.match,
                                deleted.shot.teamId,
                                deleted.periodId,
                                -1
                            );
                        } else if (e.result.match && prev.match) {
                            nextMatch = refreshMatchGoals(prev.match, e.result);
                        }
                        return { events, match: nextMatch };
                    });
                    success('Event deleted.');
                    return;
                }
                this.setState((prev) => {
                    const events = replacePendingWithServer(
                        prev.events,
                        e.clientEventKey,
                        e.result
                    );
                    const isGoal =
                        e.result.typeId === EventTypeEnum.Shot && !!e.result.shot?.isGoal;
                    return {
                        events,
                        match:
                            isGoal && e.result.match && prev.match
                                ? refreshMatchGoals(prev.match, e.result)
                                : prev.match,
                    };
                });
            }
            if (e.type === 'cancelled' && e.matchId === matchId) {
                this.setState((prev) => ({
                    events: removePendingByKey(prev.events, e.clientEventKey),
                }));
            }
        });
    };

    public componentDidUpdate = (_prevProps: Props, prevState: State): void => {
        // Persist clock/period/possession whenever they change so a refresh
        // can restore them. clockRunning is intentionally not restored: we
        // always come back paused and let the operator tap Start.
        if (
            prevState.timeRemaining !== this.state.timeRemaining ||
            prevState.shotClock !== this.state.shotClock ||
            prevState.currentPeriod !== this.state.currentPeriod ||
            prevState.overtimeMode !== this.state.overtimeMode ||
            prevState.possession !== this.state.possession
        ) {
            this.persistClock();
        }
    };

    public componentWillUnmount = (): void => {
        this.isMounted_ = false;
        this.freezeClocks();
        void this.eventsHub.disconnect();
        this.unsubOutbox?.();
        this.unsubOutbox = null;
    };

    // --- Realtime event sync (MatchHub) ---
    // Only events and score sync; clocks and possession stay local per device.

    private connectEventsHub = (): void => {
        const { matchId } = this.props.match.params;
        if (!matchId) return;
        void this.eventsHub.connectAndJoin(parseInt(matchId, 10), {
            onRemoteCreate: this.handleRemoteEventUpsert,
            onRemoteUpdate: this.handleRemoteEventUpsert,
            onRemoteDelete: this.handleRemoteEventDeleted,
            // Full refetch on join/reconnect; getMatch already fetches
            // events + match together and leaves the clock alone.
            onResync: this.getMatch,
        });
    };

    private handleRemoteEventUpsert = (event: EventVm): void => {
        this.setState((prev) => {
            if (!prev.match) return null;
            const events = upsertServerEvent(prev.events, event);
            const newMatch =
                event.typeId === EventTypeEnum.Shot && event.match
                    ? refreshMatchGoals(prev.match, event)
                    : prev.match;
            return { events, match: newMatch };
        });
    };

    private handleRemoteEventDeleted = (event: EventVm): void => {
        this.setState((prev) => {
            if (!prev.match) return null;
            const deletedEvent = prev.events.find((e: EventVm): boolean => e.id === event.id);
            if (!deletedEvent) return null; // deleted locally already (echo)
            const newEvents = applyRemoteDelete(prev.events, event);
            const newMatch =
                deletedEvent.typeId === EventTypeEnum.Shot && event.match
                    ? refreshMatchGoals(prev.match, event)
                    : prev.match;
            return { events: newEvents, match: newMatch };
        });
    };

    // --- Clock persistence (localStorage, single device) ---

    private storageKey = (): string | null => {
        const { matchId } = this.props.match.params;
        return matchId ? `twa:mobileClock:${matchId}` : null;
    };

    private persistClock = (): void => {
        const key = this.storageKey();
        if (!key) return;
        const { timeRemaining, shotClock, currentPeriod, overtimeMode, possession } = this.state;
        try {
            window.localStorage.setItem(
                key,
                JSON.stringify({
                    timeRemaining,
                    shotClock,
                    currentPeriod,
                    overtimeMode,
                    possession,
                })
            );
        } catch {
            // Storage may be full/blocked; persistence is best-effort.
        }
    };

    private restoreClock = (): void => {
        const key = this.storageKey();
        if (!key) return;
        try {
            const raw = window.localStorage.getItem(key);
            if (!raw) return;
            const snap = JSON.parse(raw);
            this.setState((prev) => ({
                timeRemaining:
                    typeof snap.timeRemaining === 'number'
                        ? snap.timeRemaining
                        : prev.timeRemaining,
                shotClock: typeof snap.shotClock === 'number' ? snap.shotClock : prev.shotClock,
                currentPeriod:
                    typeof snap.currentPeriod === 'number'
                        ? snap.currentPeriod
                        : prev.currentPeriod,
                overtimeMode: snap.overtimeMode ?? prev.overtimeMode,
                possession:
                    snap.possession === 'home' || snap.possession === 'away'
                        ? snap.possession
                        : null,
                // Always come back paused after a refresh.
                clockRunning: false,
            }));
        } catch {
            // Corrupt/unparseable snapshot: ignore and keep defaults.
        }
    };

    private clearTimer = (): void => {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    };

    // Create/resume the audio context from a user gesture (the start tap).
    private ensureAudio = (): void => {
        try {
            const Ctx =
                window.AudioContext ||
                (window as unknown as { webkitAudioContext?: typeof AudioContext })
                    .webkitAudioContext;
            if (!Ctx) return;
            if (!this.audioCtx) this.audioCtx = new Ctx();
            if (this.audioCtx.state === 'suspended') void this.audioCtx.resume();
        } catch {
            // Audio is a nice-to-have; ignore failures.
        }
    };

    // Buzzer when a clock hits 0. Main clock is lower/longer than shot clock.
    private playBuzzer = (kind: 'main' | 'shot'): void => {
        try {
            this.ensureAudio();
            const ctx = this.audioCtx;
            if (!ctx) return;
            const now = ctx.currentTime;
            const duration = kind === 'main' ? 1.4 : 0.7;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'square';
            osc.frequency.value = kind === 'main' ? 360 : 720;
            gain.gain.setValueAtTime(0.0001, now);
            gain.gain.exponentialRampToValueAtTime(0.3, now + 0.02);
            gain.gain.setValueAtTime(0.3, now + duration - 0.06);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
            osc.connect(gain).connect(ctx.destination);
            osc.start(now);
            osc.stop(now + duration);
        } catch {
            // Ignore audio errors.
        }
    };

    private getMatch = async (): Promise<void> => {
        const { matchId } = this.props.match.params;
        if (!matchId) return;

        try {
            const id = parseInt(matchId, 10);
            const [match, events] = await Promise.all([
                new MatchesClient().getById(id),
                new EventsClient().getByMatchId(id),
            ]);
            if (!this.isMounted_) return;
            this.setState(
                (prev) => {
                    const merged = mergeServerWithLocalPending(events, prev.events);
                    return {
                        match: bumpMatchForPendingGoals(match, merged),
                        events: merged,
                        loading: false,
                    };
                },
                () => {
                    void this.rehydratePendingOutbox();
                }
            );
        } catch (error) {
            if (!this.isMounted_) return;
            errorWithUserMessage('Could not load the match.');
            this.setState({ loading: false });
        }
    };

    /** Merge IndexedDB pending creates into the event log after a successful load/refresh. */
    private rehydratePendingOutbox = async (): Promise<void> => {
        const match = this.state.match;
        if (!match || !this.isMounted_) return;
        const rows = await eventSubmissionQueue.listByMatch(match.id);
        if (!this.isMounted_ || rows.length === 0) return;
        this.setState((prev) => {
            if (!prev.match) return null;
            const next = applyOutboxRowsToState(rows, prev.events, prev.match);
            return { events: next.events, match: next.match };
        });
    };

    // --- Clock engine (main clock + shot clock run/stop together) ---

    private readClocks = (): { main: number; shot: number } => {
        const main = this.mainAnchor ? readCountdown(this.mainAnchor) : this.state.timeRemaining;
        const shot = this.shotAnchor ? readCountdown(this.shotAnchor) : this.state.shotClock;
        return { main, shot };
    };

    /** Freeze wall-clock values into state and drop anchors. */
    private freezeClocks = (): { main: number; shot: number } => {
        const frozen = this.readClocks();
        this.clearTimer();
        this.mainAnchor = null;
        this.shotAnchor = null;
        return frozen;
    };

    private toggleClock = (): void => {
        if (this.state.clockRunning) {
            const { main, shot } = this.freezeClocks();
            this.setState({ timeRemaining: main, shotClock: shot, clockRunning: false });
            return;
        }
        if (this.state.timeRemaining <= 0 || this.state.shotClock <= 0) return;
        this.ensureAudio();
        const now = performance.now();
        this.mainAnchor = startCountdown(this.state.timeRemaining, now);
        this.shotAnchor = startCountdown(this.state.shotClock, now);
        this.timer = setInterval(this.tick, CLOCK_TICK_MS);
        this.setState({ clockRunning: true });
    };

    private tick = (): void => {
        const { main, shot } = this.readClocks();
        if (main <= 0 || shot <= 0) {
            this.clearTimer();
            this.mainAnchor = null;
            this.shotAnchor = null;
            this.playBuzzer(main <= 0 ? 'main' : 'shot');
            this.setState({
                timeRemaining: Math.max(0, main),
                shotClock: Math.max(0, shot),
                clockRunning: false,
            });
            return;
        }
        this.setState({ timeRemaining: main, shotClock: shot });
    };

    private stopClock = (): void => {
        if (!this.state.clockRunning && !this.mainAnchor) return;
        const { main, shot } = this.freezeClocks();
        this.setState({ timeRemaining: main, shotClock: shot, clockRunning: false });
    };

    private setPeriod = (period: EventPeriodEnum, overtimeMode: OvertimeUiMode = 'play'): void => {
        this.freezeClocks();
        this.setState({
            currentPeriod: period,
            overtimeMode: period === EventPeriodEnum.Overtime ? overtimeMode : 'play',
            timeRemaining: INITIAL_TIME,
            shotClock: SHOT_MAX,
            clockRunning: false,
        });
    };

    // --- Possession + shot-clock resets ---

    private setPossession = (side: 'home' | 'away'): void => this.setState({ possession: side });

    private reset28 = (): void => {
        const { main } = this.readClocks();
        const nextShot = clampShot(SHOT_MAX, main);
        if (this.shotAnchor) {
            this.shotAnchor = startCountdown(nextShot);
        }
        this.setState((prev) => ({
            timeRemaining: main,
            shotClock: nextShot,
            possession:
                prev.possession === 'home'
                    ? 'away'
                    : prev.possession === 'away'
                    ? 'home'
                    : prev.possession,
        }));
    };

    private reset18 = (): void => {
        const { main } = this.readClocks();
        const nextShot = clampShot(SHOT_PARTIAL, main);
        if (this.shotAnchor) {
            this.shotAnchor = startCountdown(nextShot);
        }
        this.setState({
            timeRemaining: main,
            shotClock: nextShot,
        });
    };

    // --- Clock editing (tap the clock / shot clock) ---

    private openClockEditor = (): void => {
        this.stopClock();
        this.setState({ clockEditing: 'main' });
    };

    private openShotEditor = (): void => {
        this.stopClock();
        this.setState({ clockEditing: 'shot' });
    };

    private setMainClock = (seconds: number): void => {
        const next = Math.max(0, Math.min(INITIAL_TIME, seconds));
        this.freezeClocks();
        this.setState((prev) => ({
            timeRemaining: next,
            shotClock: Math.min(prev.shotClock, next),
            clockRunning: false,
            clockEditing: null,
        }));
    };

    private setShotClock = (seconds: number): void => {
        this.setState((prev) => ({
            shotClock: clampShot(seconds, prev.timeRemaining),
            clockEditing: null,
        }));
    };

    // --- Event actions (first tap stops the clock + applies side-effects) ---

    private teamIdForSide = (side: 'home' | 'away'): number => {
        const { match } = this.state;
        return side === 'home' ? match!.homeTeamId : match!.awayTeamId;
    };

    private handleGoal = (): void => {
        const { possession } = this.state;
        if (possession === null) return;
        const { main } = this.freezeClocks();
        this.setState({
            timeRemaining: main,
            clockRunning: false,
            shotClock: clampShot(SHOT_MAX, main),
            possession: possession === 'home' ? 'away' : 'home',
            flowSide: possession,
            eventTimeRemaining: main,
            pendingFlow: 'goal',
            goalType: 'regular',
        });
    };

    private handleFoul = (): void => {
        const { possession } = this.state;
        if (possession === null) return;
        const defender = possession === 'home' ? 'away' : 'home';
        const { main, shot } = this.freezeClocks();
        this.setState({
            timeRemaining: main,
            clockRunning: false,
            // Reset to 18 only if the shot clock is currently below 18.
            shotClock: shot < SHOT_PARTIAL ? clampShot(SHOT_PARTIAL, main) : shot,
            flowSide: defender,
            eventTimeRemaining: main,
            pendingFlow: 'foul',
            foulModifier: null,
        });
    };

    private handleTimeout = (): void => {
        const { possession, match } = this.state;
        if (possession === null || !match) return;
        const { main, shot } = this.freezeClocks();
        this.setState({ timeRemaining: main, shotClock: shot, clockRunning: false });
        this.recordTimeout(this.teamIdForSide(possession), main);
    };

    private handlePickCap = (player: MatchPlayerVm): void => {
        const { pendingFlow, flowSide, goalType, foulModifier } = this.state;
        if (!flowSide || player.id === undefined) return;
        const teamId = this.teamIdForSide(flowSide);

        if (pendingFlow === 'goal') {
            this.recordShot(teamId, player.id, goalType === 'penalty');
        } else if (pendingFlow === 'foul') {
            const typeId =
                foulModifier === 'brutality'
                    ? ExclusionTypeEnum.Brutality
                    : foulModifier === 'suspension'
                    ? ExclusionTypeEnum.Suspension
                    : foulModifier === 'penalty'
                    ? ExclusionTypeEnum.Penalty
                    : ExclusionTypeEnum.Exclusion;
            this.recordExclusion(teamId, player.id, typeId);
        }
    };

    private closeFlow = (): void =>
        this.setState({ pendingFlow: null, flowSide: null, foulModifier: null });

    // --- Recording (events keep the captured tap time) ---

    private timing = (timeOverride?: number): MobileEventTiming => ({
        periodId: this.state.currentPeriod,
        timeRemaining: timeOverride ?? this.state.eventTimeRemaining,
    });

    private recordShot = (teamId: number, takenById: number, isPenalty: boolean): void => {
        const { match, events } = this.state;
        if (!match) return;
        const command = buildShotCommand(match, events, this.timing(), {
            teamId,
            takenById,
            isGoal: true,
            isPenalty,
        });
        this.submitCommand(command);
    };

    private recordExclusion = (
        teamId: number,
        excludedPlayerId: number,
        typeId: ExclusionTypeEnum
    ): void => {
        const { match, events } = this.state;
        if (!match) return;
        const command = buildExclusionCommand(match, events, this.timing(), {
            teamId,
            excludedPlayerId,
            typeId,
        });
        this.submitCommand(command);
    };

    private recordTimeout = (teamId: number, captured: number): void => {
        const { match, events } = this.state;
        if (!match) return;
        const command = buildTimeoutCommand(match, events, this.timing(captured), teamId);
        this.submitCommand(command);
    };

    /**
     * Non-blocking submit: clock side-effects are already applied on the action
     * tap. Event appears instantly (pending/red border) and is persisted via the
     * IndexedDB outbox (flush when online).
     */
    private submitCommand = (command: CreateEventCommand): void => {
        this.setState({ pendingFlow: null, flowSide: null, foulModifier: null });
        const clientEventKey = newClientEventKey();
        const optimistic = optimisticEventFromCommand(command, clientEventKey);
        const label = getEventCloudLabel(command, this.state.match);
        const payload = payloadFromCommand(command);

        this.setState((prev) => {
            let match = prev.match;
            if (command.typeId === EventTypeEnum.Shot && command.shot?.isGoal && match) {
                match = adjustMatchGoalsForShot(match, command.shot.teamId, command.periodId, 1);
            }
            return { events: [...prev.events, optimistic], match };
        });

        void eventSubmissionQueue.enqueue({
            matchId: command.matchId,
            op: 'create',
            label,
            clientEventKey,
            payload,
            optimisticEvent: optimistic,
            onCancel: () => {
                if (!this.isMounted_) return;
                this.setState((prev) => {
                    let match = prev.match;
                    if (command.typeId === EventTypeEnum.Shot && command.shot?.isGoal && match) {
                        match = adjustMatchGoalsForShot(
                            match,
                            command.shot.teamId,
                            command.periodId,
                            -1
                        );
                    }
                    return {
                        events: removePendingByKey(prev.events, clientEventKey),
                        match,
                    };
                });
            },
        });
    };

    private deleteEvent = async (event: EventVm): Promise<void> => {
        const { match } = this.state;
        if (!match) return;

        const pendingKey = getClientEventKey(event);
        if (pendingKey && isPendingEvent(event) && event.id < 0) {
            await eventSubmissionQueue.cancelByClientEventKey(pendingKey);
            success('Queued event cancelled.');
            return;
        }

        const clientEventKey = newClientEventKey();
        this.setState((prev) => ({
            events: prev.events.map((e) =>
                e.id === event.id ? markPending(e, clientEventKey) : e
            ),
        }));

        void eventSubmissionQueue.enqueue({
            matchId: match.id,
            op: 'delete',
            label: getEventCloudLabel(event, match),
            clientEventKey,
            serverEventId: event.id,
            payload: { eventId: event.id },
            onCancel: () => {
                if (!this.isMounted_) return;
                this.setState((prev) => ({
                    events: prev.events.map((e) => {
                        if (getClientEventKey(e) !== clientEventKey) return e;
                        const cleared = { ...e } as EventVm;
                        delete (cleared as any).clientEventKey;
                        delete (cleared as any).isPending;
                        return cleared;
                    }),
                }));
            },
        });
    };

    // --- Event log helpers ---

    private matchPlayers = (): MatchPlayerVm[] =>
        (this.state.match?.players as MatchPlayerVm[]) || [];

    // "#cap · Surname" for the events log.
    private playerLabel = (matchPlayerId?: number): string => {
        if (matchPlayerId === undefined) return '?';
        const player = this.matchPlayers().find((p) => p.id === matchPlayerId);
        if (!player) return '?';
        const cap = player.capNumber || player.number;
        const surname = player.player?.surname || player.player?.name || '';
        return surname ? `#${cap} · ${surname}` : `#${cap}`;
    };

    private eventTeamId = (event: EventVm): number | undefined =>
        event.shot?.teamId ?? event.exclusion?.teamId ?? event.timeout?.teamId;

    private eventSide = (event: EventVm): 'home' | 'away' | undefined => {
        const { match } = this.state;
        const teamId = this.eventTeamId(event);
        if (!match || teamId === undefined) return undefined;
        if (teamId === match.homeTeamId) return 'home';
        if (teamId === match.awayTeamId) return 'away';
        return undefined;
    };

    private periodShort = (periodId: EventPeriodEnum): string => {
        switch (periodId) {
            case EventPeriodEnum.First_Period:
                return 'Q1';
            case EventPeriodEnum.Second_Period:
                return 'Q2';
            case EventPeriodEnum.Third_Period:
                return 'Q3';
            case EventPeriodEnum.Fourth_Period:
                return 'Q4';
            case EventPeriodEnum.Overtime:
                return 'PS';
            default:
                return '';
        }
    };

    private eventTime = (event: EventVm): string =>
        `${event.minute}:${String(event.seconds).padStart(2, '0')}`;

    private eventInfo = (event: EventVm): { marker: EventMarker; text: string } => {
        switch (event.typeId) {
            case EventTypeEnum.Shot: {
                const name = this.playerLabel(event.shot?.takenById);
                const isGoal = !!event.shot?.isGoal;
                const isPenalty = event.shot?.typeId === ShotTypeEnum.Penalty;
                return {
                    marker: isGoal ? 'goal' : 'miss',
                    text: `${name}${isPenalty ? ' (penalty)' : ''}`,
                };
            }
            case EventTypeEnum.Exclusion: {
                const name = this.playerLabel(event.exclusion?.excludedPlayerId);
                const t = event.exclusion?.typeId;
                const tag =
                    t === ExclusionTypeEnum.Brutality
                        ? ' (brutality)'
                        : t === ExclusionTypeEnum.Suspension
                        ? ' (suspension)'
                        : t === ExclusionTypeEnum.Penalty
                        ? ' (penalty)'
                        : '';
                return { marker: 'foul', text: `${name}${tag}` };
            }
            case EventTypeEnum.Timeout:
                return { marker: 'timeout', text: 'Timeout' };
            default:
                return { marker: 'timeout', text: 'Event' };
        }
    };

    public render(): React.ReactElement {
        const {
            match,
            events,
            loading,
            currentPeriod,
            overtimeMode,
            timeRemaining,
            shotClock,
            clockRunning,
            possession,
            pendingFlow,
            flowSide,
            goalType,
            foulModifier,
            clockEditing,
        } = this.state;

        const onBack = (): void =>
            this.props.history.push(
                Routes.ROUTE_MOBILE_MATCH_LOG.replace(
                    ReplaceStrings.MATCH_ID,
                    this.props.match.params.matchId
                )
            );

        if (!match) {
            return (
                <MobileShell title="Livescore" onBack={onBack}>
                    <Spin spinning={loading}>{!loading && <div>Match not found.</div>}</Spin>
                </MobileShell>
            );
        }

        const players = this.matchPlayers();
        const homePlayers = players.filter((p) => p.teamId === match.homeTeamId);
        const awayPlayers = players.filter((p) => p.teamId === match.awayTeamId);
        const flowPlayers = flowSide === 'away' ? awayPlayers : homePlayers;

        // Newest game-time event on top: clock counts down, so within a period the
        // event with the least time remaining is the most recent.
        // Show every event, not just the latest few.
        const recentEvents = [...events].sort((a, b) => {
            if (a.periodId !== b.periodId) return b.periodId - a.periodId;
            const ra = a.minute * 60 + a.seconds;
            const rb = b.minute * 60 + b.seconds;
            if (ra !== rb) return ra - rb;
            return b.id - a.id;
        });

        const homeAbbr = (match.homeTeamDisplayName || 'Home').slice(0, 3).toUpperCase();
        const awayAbbr = (match.awayTeamDisplayName || 'Away').slice(0, 3).toUpperCase();

        return (
            <MobileShell
                title="Livescore"
                onBack={onBack}
                headerExtra={<OfflineStatusControl matchId={match.id} variant="mobile" />}
            >
                <div className="m-report m-report--dark">
                    <Spin spinning={loading}>
                        <ScoreHeader
                            match={match}
                            period={currentPeriod}
                            overtimeMode={overtimeMode}
                            timeRemaining={timeRemaining}
                            shotClock={shotClock}
                            clockRunning={clockRunning}
                            onEditClock={this.openClockEditor}
                            onEditShot={this.openShotEditor}
                            onSetPeriod={this.setPeriod}
                        />

                        <PossessionToggle
                            value={possession}
                            homeLabel={homeAbbr}
                            awayLabel={awayAbbr}
                            onChange={this.setPossession}
                        />

                        {pendingFlow ? (
                            <EventEntryPanel
                                flow={pendingFlow}
                                side={flowSide ?? 'home'}
                                players={flowPlayers}
                                goalType={goalType}
                                foulModifier={foulModifier}
                                onSetGoalType={(type): void => this.setState({ goalType: type })}
                                onSetFoulModifier={(modifier): void =>
                                    this.setState({ foulModifier: modifier })
                                }
                                onPick={this.handlePickCap}
                                onCancel={this.closeFlow}
                            />
                        ) : (
                            <>
                                <ShotClockControls
                                    clockRunning={clockRunning}
                                    canRun={timeRemaining > 0 && shotClock > 0}
                                    canReset18={shotClock <= SHOT_PARTIAL}
                                    onToggle={this.toggleClock}
                                    onReset28={this.reset28}
                                    onReset18={this.reset18}
                                />

                                <ActionButtons
                                    disabled={possession === null}
                                    onTimeout={this.handleTimeout}
                                    onFoul={this.handleFoul}
                                    onGoal={this.handleGoal}
                                />

                                <div className="m-events">
                                    <div className="m-events__title">Events</div>
                                    {recentEvents.length === 0 ? (
                                        <div className="m-events__empty">No events yet.</div>
                                    ) : (
                                        recentEvents.map((event, index) => {
                                            const side = this.eventSide(event);
                                            const quarterBreak =
                                                index > 0 &&
                                                event.periodId !== recentEvents[index - 1].periodId;
                                            const info = this.eventInfo(event);
                                            return (
                                                <div
                                                    key={
                                                        getClientEventKey(event) || String(event.id)
                                                    }
                                                    className={`m-event${
                                                        side ? ` m-event--${side}` : ''
                                                    }${quarterBreak ? ' m-event--qbreak' : ''}${
                                                        isPendingEvent(event)
                                                            ? ' m-event--pending'
                                                            : ''
                                                    }`}
                                                >
                                                    <span
                                                        className={`m-event__mark m-event__mark--${info.marker}`}
                                                    >
                                                        {info.marker === 'goal' ? (
                                                            <Icon name="icon-volleyball-ball" />
                                                        ) : info.marker === 'timeout' ? (
                                                            <Icon name="icon-whistle-solid" />
                                                        ) : (
                                                            '\u2715'
                                                        )}
                                                    </span>
                                                    <span className="m-event__text">
                                                        {this.periodShort(event.periodId)} ·{' '}
                                                        {this.eventTime(event)} · {info.text}
                                                    </span>
                                                    <Button
                                                        size="small"
                                                        danger
                                                        type="text"
                                                        onClick={(): void => {
                                                            this.deleteEvent(event);
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </>
                        )}
                    </Spin>

                    <ClockEditor
                        mode={clockEditing}
                        mainSeconds={timeRemaining}
                        shotSeconds={shotClock}
                        shotMax={Math.min(SHOT_MAX, timeRemaining)}
                        mainMax={INITIAL_TIME}
                        onSubmitMain={this.setMainClock}
                        onSubmitShot={this.setShotClock}
                        onCancel={(): void => this.setState({ clockEditing: null })}
                    />
                </div>
            </MobileShell>
        );
    }
}

export default MobileMatchReport;
